import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Palette } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { themeOperations } from "@/lib/db";
import { cache } from "@/lib/cache";
import { Skeleton } from "@/components/ui/skeleton";
import Layout from "@/components/Layout";
import type { Theme } from "@/types";

const THEMES_CACHE_KEY = "all_themes";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const ThemesSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: 12 }).map((_, i) => (
      <Card key={i} className="overflow-hidden">
        <Skeleton className="h-48 w-full" />
        <CardContent className="p-4">
          <Skeleton className="h-6 w-32 mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const Themes = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: themes, isLoading } = useQuery<Theme[]>({
    queryKey: ["themes"],
    queryFn: async () => {
      // Try to get from cache first
      const cachedThemes = cache.get<Theme[]>(THEMES_CACHE_KEY);
      if (cachedThemes) {
        return cachedThemes;
      }

      // Fetch from database if not in cache
      const fetchedThemes = await themeOperations.getAllThemes();
      
      // Cache the result
      cache.set(THEMES_CACHE_KEY, fetchedThemes, CACHE_TTL);
      
      return fetchedThemes;
    },
    staleTime: CACHE_TTL,
  });

  const filteredThemes = themes?.filter(theme => {
    // First filter by search term
    const matchesSearch = theme.theme_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      theme.short_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      theme.category?.toLowerCase().includes(searchTerm.toLowerCase());

    // Then filter by selected category
    const matchesCategory = selectedCategory === null || theme.category === selectedCategory;

    return matchesSearch && matchesCategory;
  }) || [];

  const categories = [...new Set(themes?.map(theme => theme.category).filter(Boolean))];

  const handleCategoryClick = (category: string) => {
    // Toggle category selection
    setSelectedCategory(selectedCategory === category ? null : category);
  };

  return (
    <Layout
      seoTitle="Browse All Themes - SKECHA"
      seoDescription="Explore our complete collection of coloring page themes. From dinosaurs to princesses, space adventures to underwater worlds - find the perfect theme for your coloring pages."
      seoKeywords="coloring page themes, kids themes, coloring book categories, birthday themes, educational themes, creative themes"
      seoUrl="https://skecha.com/themes"
      breadcrumbs={[
        { name: "Home", url: "https://skecha.com" },
        { name: "Themes", url: "https://skecha.com/themes" },
      ]}
    >
      
      <main className="pt-20 pb-20">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
              <Palette className="text-primary" />
              All Themes
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Browse our complete collection of themes to transform your photos into amazing coloring pages
            </p>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search themes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="theme-search"
              />
            </div>
          </div>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Categories</h2>
                {selectedCategory && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    Clear filter
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Badge 
                    key={category} 
                    variant={selectedCategory === category ? "default" : "secondary"}
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => handleCategoryClick(category)}
                  >
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Themes Grid */}
          {isLoading ? (
            <ThemesSkeleton />
          ) : (
            <>
              <div className="mb-6">
                <p className="text-muted-foreground">
                  Showing {filteredThemes.length} of {themes?.length || 0} themes
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory && ` in "${selectedCategory}"`}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="themes-grid">
                {filteredThemes.map((theme) => (
                  <Card key={theme.id} className="overflow-hidden group hover:shadow-lg transition-all duration-300 hover:scale-105" data-testid="theme-card">
                    <div className="relative">
                      <img
                        src={theme.sample_image_url}
                        alt={theme.theme_name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-primary flex-1">
                          {theme.theme_name}
                        </h3>
                        {theme.category && (
                          <Badge variant="outline" className="text-xs ml-2">
                            {theme.category}
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm mb-2">
                        {theme.short_description}
                      </p>
                      {theme.usage_count !== undefined && theme.usage_count > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Used {theme.usage_count} times
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredThemes.length === 0 && !isLoading && (
                <div className="text-center py-12">
                  <Palette className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No themes found</h3>
                  <p className="text-muted-foreground">
                    {searchTerm || selectedCategory
                      ? `No themes match your current filters. Try adjusting your search or category selection.`
                      : "No themes available at the moment."
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default Themes;